import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Table } from '@medprompt/shared';
import SearchBar from '../detail/tabs/SearchBar';
import AddConfiguredCodeModal from './AddConfiguredCodeModal';
import BulkDeleteConfiguredCodesModal from './BulkDeleteConfiguredCodesModal';
import EditConfiguredCodeModal from './EditConfiguredCodeModal';
import { asDisabledFilterSet } from './configTableCells';

const ConfiguredCodeSection = ({
  list,
  isEditingConfig,
  saving,
  handleCodeSearch,
  defaultHeading,
  defaultDescription,
  defaultName,
  defaultSearchPlaceholder,
  listHeading,
  listDescription,
  addButtonTitle,
  emptyMessage,
  addModalTitle,
  freeTextPlaceholder,
  removeModalTitle,
  editModalTitle,
  editInputId,
}) => (
  <>
    <div className="config-subsection">
      <h3>{defaultHeading}</h3>
      <div className="retention-description" style={{ marginBottom: '12px' }}>
        {defaultDescription}
      </div>
      <div className="measurement-code-item">
        <div className="measurement-info">
          <div className="measurement-header">
            <div className="measurement-details">
              <div className="measurement-name">{defaultName}</div>
            </div>
            <div className="measurement-controls">
              {isEditingConfig && !list.editingDefault && (
                <button
                  type="button"
                  className="other-button edit-button"
                  onClick={() => list.setEditingDefault(true)}
                  disabled={saving}
                >
                  <FontAwesomeIcon icon={faPencil} />
                </button>
              )}
              {isEditingConfig && list.editingDefault && (
                <div className="measurement-search">
                  <SearchBar
                    placeholder={defaultSearchPlaceholder}
                    onSearch={handleCodeSearch}
                    onResultClick={list.handleDefaultSelect}
                    className="measurement-search-bar"
                  />
                  <button
                    type="button"
                    className="other-button cancel-edit-button"
                    onClick={() => list.setEditingDefault(false)}
                    title="Dismiss search"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              )}
            </div>
          </div>
          {list.defaultCode && (
            <div className="current-code">
              <span className="code-id">({list.defaultCode.code})</span>
              <span className="code-term">{list.defaultCode.terms?.[0] || ''}</span>
            </div>
          )}
        </div>
      </div>
    </div>

    <div className="config-subsection clinical-dept-codes-subsection">
      <div className="clinical-dept-subsection-head">
        <div>
          <h3>{listHeading}</h3>
          <div className="retention-description">
            {listDescription}
          </div>
        </div>
        {isEditingConfig && (
          <button
            type="button"
            className="other-button config-save-button clinical-dept-add-btn"
            onClick={() => list.setModalOpen(true)}
            disabled={saving}
            title={addButtonTitle}
          >
            <FontAwesomeIcon icon={faPlus} />
            Add code
          </button>
        )}
      </div>
      <div className="clinical-dept-table-wrap config-table-container">
        <Table
          id={list.tableId}
          data={list.tableRows}
          columns={list.columns}
          getRowKey={(row) => row.code}
          sortState={list.sortState}
          onSortChange={list.setSortState}
          filterValues={list.filterValues}
          onFilterChange={list.setFilterValues}
          disabledFilters={asDisabledFilterSet(list.disabledFilters)}
          onDisabledFiltersChange={list.setDisabledFilters}
          paginationState={list.paginationState}
          onPaginationChange={list.setPaginationState}
          enableSelection={isEditingConfig}
          bulkActions={isEditingConfig ? list.bulkActions : []}
          selectedRows={list.selectedRows}
          onSelectionChange={list.setSelectedRows}
          highlightedRows={list.highlightedRows}
          onHighlightChange={list.setHighlightedRows}
          onRowClick={
            isEditingConfig && !saving
              ? list.handleRowClick
              : undefined
          }
          emptyMessage={emptyMessage}
        />
      </div>
    </div>

    <AddConfiguredCodeModal
      isOpen={list.modalOpen}
      onClose={() => list.setModalOpen(false)}
      onPick={list.handleModalPick}
      handleCodeSearch={handleCodeSearch}
      existingCodes={list.codes}
      title={addModalTitle}
      freeTextPlaceholder={freeTextPlaceholder}
    />

    <BulkDeleteConfiguredCodesModal
      isOpen={list.bulkDeleteOpen}
      onClose={() => {
        list.setBulkDeleteOpen(false);
        list.setBulkDeleteRows([]);
      }}
      onConfirm={list.confirmBulkRemove}
      rows={list.bulkDeleteRows}
      title={removeModalTitle}
    />

    <EditConfiguredCodeModal
      isOpen={!!list.editingCode}
      onClose={() => list.setEditingCode(null)}
      title={editModalTitle}
      description={
        list.editingCode && String(list.editingCode).startsWith('CUSTOM')
          ? 'Edit the label for this custom entry.'
          : 'Edit the preferred term shown for this SNOMED code.'
      }
      currentCode={list.editingCode ? list.codes[list.editingCode] : null}
      onSave={list.handleCodeSave}
      inputId={editInputId}
    />
  </>
);

export default ConfiguredCodeSection;
